import { api, ApiError, type CampaignDetail, type CampaignRun } from "../../api/client";
import { docxName, exportCampaignDocx, type AutomationEvidence, type ExportRun } from "./exportDocx";
import type { Marker, RunReport } from "./report";

/**
 * Gathers everything a realm's runs recorded (each run's report and markers,
 * and a playbook run's steps, test behaviour and device comparison) and
 * exports it to Word: every finished run, or just the ones asked for.
 */
export async function exportRealmToWord(prefix: string, detail: CampaignDetail, opts: { only?: string[]; agentNames: Record<string, string>; deviceNames?: Record<string, string> }): Promise<void> {
  const { campaign, cases, runs } = detail;
  const chosen = runs.filter((r) => (opts.only ? opts.only.includes(r.id) : !!r.stopped_at)).sort((a, b) => a.started_at.localeCompare(b.started_at));
  const optional = async <T,>(path: string): Promise<T | null> => {
    try { return await api.get<T>(path); } catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null; throw e; }
  };
  const options = await optional<{ devices?: { id: string; name: string }[]; templates?: { id: string; name: string }[] }>(`${prefix}/automation/options`);
  const deviceNames = { ...Object.fromEntries((options?.devices ?? []).map((d) => [d.id, d.name])), ...(opts.deviceNames ?? {}) };
  const templateNames = Object.fromEntries((options?.templates ?? []).map((t) => [t.id, t.name]));
  const entries: ExportRun[] = [];
  for (const r of chosen) {
    const full = await api.get<{ run: CampaignRun; report?: RunReport; markers?: Marker[] }>(`${prefix}/campaign-runs/${r.id}`);
    const automation = await optional<AutomationEvidence>(`${prefix}/automation/runs/${r.id}`);
    entries.push({
      caseName: cases.find((c) => c.id === r.case_id)?.name ?? "Deleted playbook",
      number: runs.filter((x) => x.case_id === r.case_id && x.started_at <= r.started_at).length,
      run: full.run, report: full.report ?? null, markers: full.markers ?? [],
      automation: automation && typeof automation.state === "string" ? { ...automation, steps: automation.steps ?? [], comparison: automation.comparison ?? [], behavior: automation.behavior ?? [] } : null,
    });
  }
  for (const e of entries) {
    const b = entries.find((x) => x.run.id === e.run.baseline_run_id);
    if (b?.report) e.baseline = { label: `${b.caseName} — run ${b.number}`, report: b.report };
  }
  const suffix = opts.only?.length === 1 && entries[0] ? `${entries[0].caseName}-run-${entries[0].number}` : "";
  await exportCampaignDocx(campaign, campaign.agent_ids.map((a) => opts.agentNames[a] ?? a.slice(0, 8)), entries, docxName(campaign.name, suffix), {
    deviceName: (id) => deviceNames[id] ?? id.slice(0, 8),
    templateName: (id) => templateNames[id] ?? id.slice(0, 8),
  });
}
